import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface CartItem {
  id: string;
  name: string;
  price: number;
  image: string;
  quantity: number;
}

interface CartProps {
  items: CartItem[];
  onClose: () => void;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemoveItem: (id: string) => void;
  onCheckout: () => void;
}

export default function Cart({ items, onClose, onUpdateQuantity, onRemoveItem, onCheckout }: CartProps) {
  const [showCheckout, setShowCheckout] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerData, setCustomerData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: ''
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(price);
  };

  const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleQuantityChange = (id: string, newQuantity: number) => {
    if (newQuantity > 0) {
      onUpdateQuantity(id, newQuantity);
    }
  };

  const handleClearCart = () => {
    items.forEach(item => onRemoveItem(item.id));
  };

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (items.length === 0) return;

    setIsSubmitting(true);

    try {
      // Guardar el pedido en Supabase
      const orderData = {
        customer_name: customerData.name,
        customer_phone: customerData.phone,
        customer_email: customerData.email,
        customer_address: customerData.address,
        items: items.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        total: total,
        status: 'pending',
        notes: customerData.notes || null
      };

      const { error: orderError } = await supabase
        .from('orders')
        .insert([orderData]);

      if (orderError) {
        console.error('Error saving order:', orderError);
        // Continuar con el envío aunque falle el guardado
      }

      // Crear mensaje para WhatsApp con formato elegante y profesional
      const orderDetails = items.map(item => 
        `   • ***${item.name}***\n     *Cantidad:* ${item.quantity} | *Subtotal:* ${formatPrice(item.price * item.quantity)}`
      ).join('\n\n');

      const message = `🛍️ ***NUEVO PEDIDO - PERRA LUXURY***\n\n` +
        `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
        `👤 ***INFORMACIÓN DEL CLIENTE***\n\n` +
        `   📝 *Nombre:* ***${customerData.name}***\n` +
        `   📞 *Teléfono:* ***${customerData.phone}***\n` +
        `   📧 *Email:* ***${customerData.email}***\n` +
        `   📍 *Dirección:* ***${customerData.address}***\n\n` +
        `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
        `🛒 ***DETALLE DEL PEDIDO***\n\n${orderDetails}\n\n` +
        `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
        `💰 ***TOTAL A PAGAR: ${formatPrice(total)}***\n\n` +
        `${customerData.notes ? `📝 ***NOTAS ESPECIALES***\n   ${customerData.notes}\n\n` : ''}` +
        `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
        `✨ *Cliente listo para coordinar pago y envío*\n` +
        `🚚 *Envío por Uber Express*`;

      // Enviar por WhatsApp
      const whatsappUrl = `https://wa.me/5491155857757?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');

      // Mostrar mensaje de éxito
      setShowSuccess(true);
      
      // Después de 3 segundos, limpiar y cerrar
      setTimeout(() => {
        handleClearCart();
        setShowCheckout(false);
        setShowSuccess(false);
        onClose();
        
        // Reset form
        setCustomerData({
          name: '',
          phone: '',
          email: '',
          address: '',
          notes: ''
        });
      }, 3000);

    } catch (error) {
      console.error('Error al enviar el pedido:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mensaje de éxito
  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-50 overflow-hidden">
        <div className="absolute inset-0 bg-black/90 backdrop-blur-sm"></div>
        
        <div className="absolute right-0 top-0 h-full w-full max-w-md bg-gray-900 shadow-2xl flex items-center justify-center">
          <div className="text-center p-8">
            <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-4xl text-white"></i>
            </div>
            <h3 className="text-2xl font-thin text-white mb-4 uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
              ¡ENVIADO CON ÉXITO!
            </h3>
            <p className="text-gray-300 text-lg font-thin leading-relaxed mb-6" style={{ fontFamily: '"Lato", sans-serif' }}>
              Tu pedido fue enviado a <span className="text-[#C6A45C] font-medium">Macarena</span>.
            </p>
            <p className="text-gray-400 text-base font-thin" style={{ fontFamily: '"Lato", sans-serif' }}>
              Interactúa con ella por WhatsApp para culminar la compra.
            </p>
            <div className="mt-8">
              <div className="flex items-center justify-center space-x-2 text-green-400">
                <i className="ri-whatsapp-line text-2xl"></i>
                <span className="font-thin" style={{ fontFamily: '"Lato", sans-serif' }}>
                  Redirigiendo a WhatsApp...
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-gray-900 shadow-2xl transform transition-transform duration-300 ease-in-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <h2 className="text-2xl font-thin text-white uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
              CARRITO DE COMPRAS
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors duration-300"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {!showCheckout ? (
            <>
              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-6">
                {items.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-shopping-cart-line text-6xl text-gray-600 mb-4"></i>
                    <p className="text-gray-400 text-lg font-thin" style={{ fontFamily: '"Lato", sans-serif' }}>
                      TU CARRITO ESTÁ VACÍO
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {items.map((item) => (
                      <div key={item.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                        <div className="flex items-center space-x-4">
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div className="flex-1">
                            <h3 className="text-white font-thin text-sm uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
                              {item.name}
                            </h3>
                            <p className="text-[#C6A45C] font-thin text-lg" style={{ fontFamily: '"Lato", sans-serif' }}>
                              {formatPrice(item.price)}
                            </p>
                          </div>
                          <button 
                            onClick={() => onRemoveItem(item.id)}
                            className="text-red-400 hover:text-red-300 transition-colors duration-300"
                          >
                            <i className="ri-delete-bin-line text-lg"></i>
                          </button>
                        </div>
                        
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center space-x-3">
                            <button 
                              onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                              className="w-9 h-9 bg-[#C6A45C] text-black rounded-full flex items-center justify-center hover:bg-yellow-500 transition-all duration-300 font-thin text-base shadow-lg"
                            >
                              <i className="ri-subtract-line text-sm"></i>
                            </button>
                            <span className="text-[#C6A45C] font-thin min-w-[2.5rem] text-center text-base bg-gray-700 px-3 py-1 rounded border border-gray-600" style={{ 
                              fontFamily: '"Lato", sans-serif',
                              textShadow: '0 0 10px rgba(198, 164, 92, 0.5)'
                            }}>
                              {item.quantity}
                            </span>
                            <button 
                              onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                              className="w-9 h-9 bg-[#C6A45C] text-black rounded-full flex items-center justify-center hover:bg-yellow-500 transition-all duration-300 font-thin text-base shadow-lg"
                            >
                              <i className="ri-add-line text-sm"></i>
                            </button>
                          </div>
                          <p className="text-white font-thin text-lg" style={{ fontFamily: '"Lato", sans-serif' }}>
                            {formatPrice(item.price * item.quantity)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              {items.length > 0 && (
                <div className="border-t border-gray-700 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-xl font-thin text-white uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
                      TOTAL:
                    </span>
                    <span className="text-2xl font-thin text-[#C6A45C]" style={{ 
                      fontFamily: '"Lato", sans-serif',
                      textShadow: '0 0 15px rgba(198, 164, 92, 0.4)'
                    }}>
                      {formatPrice(total)}
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    <button 
                      onClick={() => setShowCheckout(true)}
                      className="w-full bg-[#C6A45C] text-black py-3 rounded-lg hover:bg-[#B8954F] transition-all duration-300 font-thin uppercase tracking-wider whitespace-nowrap"
                      style={{ fontFamily: '"Lato", sans-serif' }}
                    >
                      PROCEDER AL CHECKOUT
                    </button>
                    <button 
                      onClick={handleClearCart}
                      className="w-full bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600 transition-all duration-300 font-thin uppercase tracking-wider whitespace-nowrap"
                      style={{ fontFamily: '"Lato", sans-serif' }}
                    >
                      VACIAR CARRITO
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Checkout Form */
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-6">
                <button 
                  onClick={() => setShowCheckout(false)}
                  className="flex items-center text-[#C6A45C] hover:text-[#B8954F] transition-colors duration-300 font-thin"
                  style={{ fontFamily: '"Lato", sans-serif' }}
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  VOLVER AL CARRITO
                </button>
              </div>

              {/* Información de envío y confirmación */}
              <div className="mb-6 bg-gray-800 border border-[#C6A45C]/30 rounded-lg p-4 max-h-32 overflow-y-auto">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#C6A45C] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-black text-sm"></i>
                  </div>
                  <div className="text-sm font-thin leading-relaxed" style={{ fontFamily: '"Lato", sans-serif' }}>
                    <p className="text-[#C6A45C] font-medium mb-2 uppercase tracking-wider">INFORMACIÓN IMPORTANTE</p>
                    <div className="text-gray-300 space-y-2">
                      <p>• El envío se realizará por <span className="text-[#C6A45C] font-medium">Uber Express</span> a cargo del comprador.</p>
                      <p>• Antes de enviar el pedido se confirmarán los datos por medio de <span className="text-[#C6A45C] font-medium">WhatsApp</span>.</p>
                      <p>• Uno de nuestros representantes lo convalidará contigo.</p>
                      <p>• Te recomendamos que prestes atención al <span className="text-[#C6A45C] font-medium">link de seguimiento</span> que te enviarán por WhatsApp.</p>
                      <p>• Mantente atento a la llegada del Uber, es muy importante para que se concrete la entrega de manera satisfactoria sin ningún problema.</p>
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={handleCheckout} className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-thin mb-2 uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
                    NOMBRE COMPLETO *
                  </label>
                  <input 
                    type="text"
                    name="name"
                    required
                    value={customerData.name}
                    onChange={(e) => setCustomerData({...customerData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300 font-thin"
                    style={{ fontFamily: '"Lato", sans-serif' }}
                    placeholder="Tu nombre completo"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-thin mb-2 uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
                    TELÉFONO *
                  </label>
                  <input 
                    type="tel"
                    name="phone"
                    required
                    value={customerData.phone}
                    onChange={(e) => setCustomerData({...customerData, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300 font-thin"
                    style={{ fontFamily: '"Lato", sans-serif' }}
                    placeholder="+54 9 11 1234-5678"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-thin mb-2 uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
                    EMAIL *
                  </label>
                  <input 
                    type="email"
                    name="email"
                    required
                    value={customerData.email}
                    onChange={(e) => setCustomerData({...customerData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300 font-thin"
                    style={{ fontFamily: '"Lato", sans-serif' }}
                    placeholder="tu@email.com"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-thin mb-2 uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
                    DIRECCIÓN DE ENTREGA *
                  </label>
                  <input 
                    type="text"
                    name="address"
                    required
                    value={customerData.address}
                    onChange={(e) => setCustomerData({...customerData, address: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300 font-thin"
                    style={{ fontFamily: '"Lato", sans-serif' }}
                    placeholder="Calle, número, ciudad"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-thin mb-2 uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
                    NOTAS ADICIONALES
                  </label>
                  <textarea 
                    name="notes"
                    value={customerData.notes}
                    onChange={(e) => setCustomerData({...customerData, notes: e.target.value})}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#C6A45C] focus:outline-none transition-colors duration-300 resize-none font-thin"
                    style={{ fontFamily: '"Lato", sans-serif' }}
                    rows={3}
                    placeholder="Instrucciones especiales, horarios de entrega, etc."
                  />
                  <p className="text-gray-400 text-xs mt-1 font-thin" style={{ fontFamily: '"Lato", sans-serif' }}>
                    {customerData.notes.length}/500 caracteres
                  </p>
                </div>

                <div className="border-t border-gray-700 pt-4 mt-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-thin text-white uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
                      TOTAL A PAGAR:
                    </span>
                    <span className="text-xl font-thin text-[#C6A45C]" style={{ 
                      fontFamily: '"Lato", sans-serif',
                      textShadow: '0 0 15px rgba(198, 164, 92, 0.4)'
                    }}>
                      {formatPrice(total)}
                    </span>
                  </div>
                  
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-all duration-300 font-thin flex items-center justify-center space-x-2 whitespace-nowrap uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: '"Lato", sans-serif' }}
                  >
                    <i className="ri-whatsapp-line text-lg"></i>
                    <span>{isSubmitting ? 'ENVIANDO...' : 'ENVIAR PEDIDO POR WHATSAPP'}</span>
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}